/**
 * /admin/migrations — list every backend/scripts/migrate_*.{sql,py}
 * file and one-click email it to tech@eyvo.com with a CSV snapshot of
 * any data already in the touched tables. Lets the operator pull a
 * "send to prod ops" lever any time without leaving Pulse.
 *
 * Session 87 pt3 — added as part of the Mongo → SQL cutover so future
 * preview-side schema changes round-trip to prod ops automatically.
 */
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import AppLayout from "@/components/layout/AppLayout";

export default function MigrationsPage() {
  const [scripts, setScripts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState({});
  const [noteByFile, setNoteByFile] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/migrations/list");
      setScripts(r.data.scripts || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load migrations");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const sendOne = async (s) => {
    setSending((m) => ({ ...m, [s.filename]: true }));
    try {
      const body = {
        filename: s.filename,
        recipient: "tech@eyvo.com",
        note: (noteByFile[s.filename] || "").trim() || null,
      };
      const r = await api.post("/admin/migrations/notify", body);
      const rows = Object.entries(r.data.row_counts || {})
        .filter(([, v]) => v > 0)
        .map(([t, v]) => `${t}: ${v}`).join(", ") || "no table data";
      toast.success(`Sent to ${r.data.recipient} — ${rows}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Send failed");
    } finally {
      setSending((m) => ({ ...m, [s.filename]: false }));
    }
  };

  return (
    <AppLayout>
      <div className="p-8 max-w-5xl" data-testid="migrations-page">
        <h1 className="text-2xl font-bold text-[#0A0A0A] mb-1">Migrations</h1>
        <p className="text-sm text-[#737373] mb-6">
          Every <code className="bg-[#F5F5F5] px-1">migrate_*.sql</code> / <code className="bg-[#F5F5F5] px-1">migrate_*.py</code> script
          under <code className="bg-[#F5F5F5] px-1">backend/scripts/</code>. Click <strong>Send to tech@eyvo.com</strong> to
          email the script + a CSV snapshot of any data already in the touched
          tables, so the same migration can be reproduced on production.
        </p>

        {loading ? (
          <div className="text-sm text-[#A3A3A3]">Loading…</div>
        ) : (
          <div className="border border-[#E5E5E5] rounded-md overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-[#F5F5F5] text-[11px] uppercase tracking-wider text-[#525252]">
                <tr>
                  <th className="px-3 py-2 text-left">Filename</th>
                  <th className="px-3 py-2 text-left w-20">Kind</th>
                  <th className="px-3 py-2 text-right w-24">Size</th>
                  <th className="px-3 py-2 text-left w-44">Modified (UTC)</th>
                  <th className="px-3 py-2 text-left">Optional note</th>
                  <th className="px-3 py-2 w-44"></th>
                </tr>
              </thead>
              <tbody>
                {scripts.map((s) => (
                  <tr key={s.filename} className="border-t border-[#F5F5F5] hover:bg-[#FAFAFA]" data-testid={`migration-row-${s.filename}`}>
                    <td className="px-3 py-2 font-mono text-[12px]">{s.filename}</td>
                    <td className="px-3 py-2 text-[#525252]">{s.kind}</td>
                    <td className="px-3 py-2 text-right text-[#525252]">{(s.size_bytes / 1024).toFixed(1)} KB</td>
                    <td className="px-3 py-2 text-[#525252] font-mono text-[11px]">
                      {new Date(s.modified_at * 1000).toISOString().replace("T", " ").slice(0, 19)}
                    </td>
                    <td className="px-3 py-2">
                      <input
                        type="text"
                        value={noteByFile[s.filename] || ""}
                        onChange={(e) => setNoteByFile((m) => ({ ...m, [s.filename]: e.target.value }))}
                        placeholder="Context for the recipient (optional)"
                        className="w-full text-xs px-2 py-1 border border-[#E5E5E5] rounded-none"
                        data-testid={`migration-note-${s.filename}`}
                      />
                    </td>
                    <td className="px-3 py-2 text-right">
                      <button
                        onClick={() => sendOne(s)}
                        disabled={!!sending[s.filename]}
                        className="text-[11px] font-bold uppercase tracking-wider text-white bg-[#0055FF] hover:bg-[#0044CC] px-3 py-1.5 disabled:opacity-50"
                        data-testid={`migration-send-${s.filename}`}
                      >
                        {sending[s.filename] ? "Sending…" : "Send to tech@eyvo.com"}
                      </button>
                    </td>
                  </tr>
                ))}
                {scripts.length === 0 && (
                  <tr><td colSpan={6} className="px-3 py-8 text-center text-[#A3A3A3]">No migration scripts found.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </AppLayout>
  );
}
